import { useState } from 'react';
import { FileText } from 'lucide-react';
import Button from '../ui/Button';
import inductionService from '../../services/induction';
import { PolicyDocumentsPanel } from '../../pages/hr/whs/WhsDocuments';

/**
 * The written policies behind the settings on Company Policies — hours, breaks,
 * leave. They are acknowledged through the induction pack like a WHS policy,
 * but they are employment documents and live here, beside the figures they
 * describe. Their {{markers}} are filled from the org's schedule and these
 * settings whenever they are read, so the text follows the settings; a change
 * that alters a published policy's words re-issues it (server-side).
 */
export default function EmploymentPolicyDocuments() {
  const [reloadKey, setReloadKey] = useState(0);
  const [installing, setInstalling] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const installStarters = async () => {
    setInstalling(true);
    setMessage(null);
    try {
      const created = await inductionService.installPolicyTemplates([], 'employment');
      setMessage(created.length
        ? `Added as drafts: ${created.map((c) => c.title).join(', ')}. Review them, fill in the italic parts, then set them to Current.`
        : 'The starter policies are already here.');
      setReloadKey((k) => k + 1);
    } catch (err) {
      setMessage(err instanceof Error && err.message ? err.message : 'Could not add the starter policies.');
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-3">
        <p className="min-w-0 flex-1 text-sm text-neutral-600">
          The written policies staff read and acknowledge. Their hours, lunch and leave figures come from your
          default work schedule and the settings on this page, and stay in step with them. If a change alters a
          published policy's wording, staff are asked to acknowledge it again. The timesheet page's summary links here too.
        </p>
        <Button variant="secondary" onClick={installStarters} isLoading={installing}>
          <FileText className="h-4 w-4" /> Add starter policies
        </Button>
      </div>
      {message && <p className="rounded-lg bg-neutral-50 border border-neutral-200 p-3 text-sm text-neutral-700">{message}</p>}
      <PolicyDocumentsPanel section="employment" reloadKey={reloadKey} />
    </div>
  );
}
